import type { ShowProductsMessage } from '@coldpress-voice/shared-types';
import { PipecatClient } from '@pipecat-ai/client-js';
import { WebSocketTransport } from '@pipecat-ai/websocket-transport';
import { useCallback, useEffect, useRef } from 'react';
import { useDemoStore } from '../store';
import { API_BASE, voiceWsUrl, warmupSession } from './api';
import { applyLimiter } from './botAudioLimiter';
import {
  CARD_MODAL_MSG_TYPE,
  buildCardModalData,
  scanMicActionOnClose,
  scanMicActionOnOpen,
} from './cardModal';
import { parseCategoryGrid } from './categoryGrid';
import {
  COMPOSER_DRAFT_MSG_TYPE,
  type ComposerDraftField,
  buildComposerDraftData,
} from './composerDraft';
import { buildContactFormPayload, parseContactFieldEcho } from './contactFields';
import { applyPreBufferForModel, parseActiveTtsModel } from './jitterBuffer';
import { labelForCode, ttsModelForName } from './languages';
import { parsePulseControl } from './pulseControl';
import { parseSceneName } from './scene';
import { parseOrderCommitted, parseSendState } from './sendState';
import { parseGapNote, parseHandoff } from './synergyR11';
import { useAudioLevels } from './useAudioLevels';
import { USER_TEXT_MSG_TYPE, buildUserTextData } from './userText';

/**
 * Voice-session lifecycle over the Pipecat WebSocket transport (ADR-002 / item 1 — replaces the
 * LiveKit client). connect → mic capture + bot-audio playback handled by the client; callbacks feed
 * the store: transcripts, the `ui.show_products` cards (server message), and audio levels for the
 * voice-synced animation (frontend-spec). The client speaks the same Protobuf frame protocol as the
 * Pipecat FastAPIWebsocketTransport server (apps/agent-worker/agent/server.py).
 *
 * Round 8 — WS drop recovery (Bug 1 fix):
 *   An unexpected WS drop (network loss, Container restart, CF eviction) previously left the store
 *   as 'idle' with governance overlays still rendered and no reconnect affordance.
 *   Fix: `cleanDisconnectRef` flag distinguishes a user-initiated hang-up (→ 'idle' + reset)
 *   from an unexpected drop (→ 'dropped' + clear governance + show reconnect/start-over UI).
 *
 * R18 Wave 1 — three hardening fixes (voice-client-contract.md):
 *   W1-1 DISTURBANCE: half-duplex mic mute during TTS. `onBotStartedSpeaking` mutes the mic;
 *     `onBotStoppedSpeaking` schedules a DEBOUNCED re-enable (400ms) that is CANCELLED if
 *     `onBotStartedSpeaking` fires again before it elapses (i.e. Gigi continued to the next
 *     sentence). Net effect: ONE mute at the start of Gigi's turn, ONE unmute when she is
 *     truly done — not N toggles per sentence. The per-sentence rapid enable/disable was the
 *     root cause of the "track-started event received for current track and already recording"
 *     console spam (×8–×17 per multi-sentence TTS response). Only unmutes if the user has not
 *     manually muted/paused (`paused` or `micEnabled === false` in the store). Barge-in
 *     (W2-8) stays parked.
 *     R21 UPDATE — turn-span mute (replaces the 400ms debounce): the 400ms debounce was sized
 *     for inter-SENTENCE gaps, but multi-utterance search turns (filler → [search, seconds] →
 *     results) have multi-SECOND inter-utterance gaps. The 400ms timer fired mid-turn → mic
 *     toggled off→on→off → DailyMediaManager released+re-acquired the track ("ended → retry 7s")
 *     → audio destabilized → Gigi dropped (DIAGNOSIS-mic-track-churn.md). Fix: mic re-enable is
 *     now driven by the server `ui.listening` signal (emitted once at true turn-end, after the
 *     final TTS utterance, no tool in-flight, no filler pending). `onBotStoppedSpeaking` arms a
 *     LONG safety-net timer (SAFETY_NET_MIC_REENABLE_MS = 9s) rearmed on each stopped event, so
 *     the mic can never get stuck off if the signal is dropped. The safety-net is cancelled when
 *     `ui.listening` arrives. See voice-client-contract.md §12 (R21).
 *   W1-2 BREAKAGE: 20s client keepalive ping. A `setInterval` in `onConnected` sends an RTVI
 *     client message (`type:'ping'`) every 20s to keep the Cloudflare WS proxy alive (idle cap
 *     ~100s). Cleared in `onDisconnected` and on unmount. Inbound `server.ping` is already
 *     silently ignored by the fall-through in `onServerMessage`.
 *   W1-4 BREAKAGE: per-session UUID. `crypto.randomUUID()` is minted once per `connect()` call
 *     and appended as `?session=<uuid>` to the WS URL, giving each kiosk visit its own Durable
 *     Object container. Close code 4001 ("replaced by new session") is not surface-able through
 *     the PipecatClient/WebSocketTransport API (the transport wraps ReconnectingWebSocket and
 *     feeds all close events into its connectionError path without exposing the code). Any
 *     reconnect — user-triggered from the 'dropped' state or future W2-1 auto-reconnect — calls
 *     `connect()` again and mints a fresh UUID, satisfying the "new UUID on 4001" contract.
 *
 * R18 Wave 1.5 — three live-test-driven fixes (voice-client-contract.md §4–6):
 *   P1 TAP-TO-SELECT: `sendTapToSelect(sku, name)` and `sendTapToDeselect(sku, name)` emit RTVI
 *     client messages `tap-to-select` / `tap-to-deselect` so the server's existing handler fires.
 *     `ui.selection_ack` responses drive `ackedSelections` in the store — the chip tray follows
 *     server-confirmed state rather than purely optimistic local state.
 *   P3 CARD RESYNC: on `onConnected`, a `request-showcase-resync` client message is sent. The
 *     server re-emits the last `ui.show_products` payload, self-healing the first-card RTVI race.
 *   P6 CONTACT / CLOSE EMAIL: `sendContactForm(fields)` emits `submit-contact` via the WS. The
 *     `ui.contact_ack` response drives `closeEmailAck` in the store (sent/queued vs error). R12:
 *     the standalone ContactCaptureForm + `ui.open_contact_form` signal were RETIRED — email capture
 *     now lives on the S7 close card (scene:'close'). The submit path is unchanged; only the UI host
 *     moved.
 *
 * R18 Wave 1.8 — conversation quality fixes (voice-client-contract.md §8–9):
 *   1.8-C1 UI.OPEN_CONTACT_FORM: RETIRED in R12 (the transcript contact form was removed in R11;
 *     the signal hit a dead UI path in the field). Email capture is the S7 close card now.
 *   1.8-C2 SELECTION BURST: No change needed — `ui.selection_ack` messages from the server each
 *     call `applySelectionAck` which does an immutable spread-update via Zustand `set()`. Zustand
 *     batches synchronous `set()` calls within a single event-loop tick, so a burst of 2–3 acks
 *     arriving close together produces at most one re-render per tick, with no flicker or per-tap
 *     spoken chatter (that chatter was server-side; the server now batch-confirms, §8).
 *
 * R18 Wave 1.6 — warm-up handshake (voice-client-contract.md §7, fix cold-start 1006 drop):
 *   ENTRY → WARMING → READY → CONNECT state machine (never opens the WS on a cold container):
 *   `startWarming()` mints ONE uuid, stores it in `warmSessionKeyRef`, transitions to 'warming',
 *   then polls GET /v1/session/warmup?session=<uuid> (via api.warmupSession) every 2s (up to 55s).
 *   On ready: auto-proceeds to `connect(warmSessionKeyRef.current)` — the WS `?session=` receives
 *   the SAME uuid that was used for warmup, so both hit the same Durable Object → same Container
 *   (one key → one DO instance → one Container, per the contract's same-UUID guarantee).
 *   On timeout: transitions to 'warmup-timeout' and shows a graceful "Taking longer — Retry" UI.
 *   On abort (user hits Start over): the AbortController cancels the in-flight fetch, the poll
 *   loop exits, and the session returns to 'idle'.
 *   Reconnect after a drop: `startWarming()` mints a fresh uuid (new session), re-runs warmup,
 *   then reconnects — consistent with the contract's reconnect behaviour (new UUID, not old one).
 *
 * R18 Wave 1.7 — auto-reconnect + connect timeout (1.7-C2):
 *   AUTO-RECONNECT: On an UNEXPECTED disconnect (status → 'dropped') the hook now automatically
 *   re-runs the warm-up→connect flow, REUSING THE SAME session uuid (voice-client-contract.md
 *   §1 + §7 "resume reconnect") so the reconnect lands the still-warm container and the server's
 *   identity guard (1.7-S1) recognises it as a resume rather than a new visitor. The old socket
 *   gets close code 4001 as a normal supersede artefact — this MUST NOT be surfaced as an error.
 *   `autoReconnectRetryCountRef` counts attempts; `AUTO_RECONNECT_MAX_RETRIES` caps at 3.
 *   Backoff: 1s → 2s → 4s (exponential, powers of 2). Status is 'reconnecting' during retries
 *   so the UI shows "Reconnecting…" rather than the dropped panel. Only after all retries fail
 *   does the store transition to 'dropped' and the manual panel appear.
 *   `autoReconnectInProgressRef` guards the `onDisconnected` path so an intermediate disconnect
 *   during a retry (e.g. the old client tearing down) does not re-enter the retry loop.
 *   Does NOT auto-reconnect on a clean user-initiated disconnect (cleanDisconnectRef = true).
 *
 *   CONNECT TIMEOUT: if `client.connect()` hasn't resolved (bot-ready) within
 *   CONNECT_TIMEOUT_MS (20s, bumped from 13s in r18-reconnect-audiodebug — borderline cold
 *   containers that the warmup poll declared ready can still take 14-18s to bind the WS port),
 *   the client is torn down and the attempt is treated as a failed connect attempt (fed into the
 *   same retry path). A long 'connecting' hang is thus always bounded; the user never sees a
 *   frozen "connecting" screen indefinitely. The timeout is cleared immediately on success.
 *
 * R18 reconnect-audiodebug — three live-session-driven fixes:
 *   RC-1 RECONNECT VIA WARM-UP: every reconnect (auto + manual retry) now goes through the SAME
 *     warm-up→connect flow as a fresh start. `startWarming()` accepts an optional `existingKey`
 *     param — when provided, reuses that UUID (same container resume) instead of minting a new one.
 *     The auto-reconnect path (onDisconnected retry + connect() catch-block retry) and the manual
 *     retry from the 'error' state both call `startWarming(existingUuid)` rather than `connect()`
 *     directly. This prevents cold-container connect-timeout loops on the first retry after a drop.
 *     CONNECT_TIMEOUT_MS also bumped 13s → 20s (see above).
 *   RC-2 TRANSCRIPT AFTER RECONNECT: on reconnect, any non-final transcript lines left over from
 *     the dropped session are finalized in the store (they will never receive a completion callback
 *     from the dead client). This prevents dangling partial-bubble entries from blocking the new
 *     session's transcript from rendering. The new client's onBotTtsText/onBotStartedSpeaking
 *     callbacks write new lines with fresh IDs, which upsertLine appends normally.
 *   RC-3 ?audiodebug=1: the worklet's debug-mode instrumentation (in the pnpm patch:
 *     StreamProcessor posts `{event:'debug', underruns, bufferedMs, isPreBuffering, floorFrames,
 *     targetFrames}` every ~10s when `debugMode = true`) is activated and consumed from the main
 *     thread. R31: the debug payload now carries the warm-up ramp's FLOOR + TARGET (frames) beside
 *     the live bufferedMs, so a field pass can VERIFY the ramp — playback should release at the
 *     floor and bufferedMs should climb toward the target while playing. The patch intercepts the
 *     'debug' message in WavStreamPlayer._start()'s port.onmessage and sends
 *     `{event:'debugmode', enabled:true}` immediately after creating the streamNode — both gated on
 *     `globalThis.location?.search?.includes?.('audiodebug')` so they are completely inert in
 *     production. The console.warn line format (R31) is:
 *       [audiodebug] <reason> underruns=N bufferedMs=M floor=F target=T preBuffering=bool
 *
 * ⚑ End-to-end audio is validated on real CF (item-6 gate): it needs the deployed Container + a mic.
 */
/** Maximum number of automatic reconnect attempts before showing the manual dropped panel. */
const AUTO_RECONNECT_MAX_RETRIES = 3;

/**
 * Connect timeout: if client.connect() hasn't resolved (bot-ready) within this window,
 * we tear it down and treat the attempt as failed (feeds into the retry path).
 * 20s: bumped from 13s (r18-reconnect-audiodebug) — a borderline cold container that
 * the warm-up poll declared ready but hadn't fully bound its port can take 14-18s to
 * complete the WS handshake; 13s caused false timeouts on first-retry after a drop.
 */
const CONNECT_TIMEOUT_MS = 20_000;

export function useVoice() {
  const clientRef = useRef<PipecatClient | null>(null);
  const sinkRef = useRef<HTMLDivElement | null>(null); // retained for layout compat; client self-plays
  const userLine = useRef<{ id: string; finals: string } | null>(null);
  const botLine = useRef<{ id: string; text: string } | null>(null);
  /**
   * Tracks whether the current disconnect is user-initiated (hang-up) vs. unexpected (WS drop).
   * Set to true before calling client.disconnect(); cleared on connect.
   * onDisconnected checks this flag: clean → 'idle' + reset(); unexpected → 'dropped' + clear governance.
   */
  const cleanDisconnectRef = useRef(false);

  /**
   * W1-2: keepalive interval handle. Set in onConnected; cleared in onDisconnected and on unmount.
   * NodeJS.Timeout typing not available in browser context — use ReturnType<typeof setInterval>.
   */
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * W1-1: tracks whether the bot (not the user) muted the mic during TTS. Guards the re-enable
   * in onBotStoppedSpeaking so we never un-mute a mic the user deliberately silenced.
   */
  const botMutedRef = useRef(false);

  /**
   * R21 safety-net: handle for the pending mic re-enable timer set in onBotStoppedSpeaking.
   * The primary re-enable driver is the server `ui.listening` signal (handled in onServerMessage).
   * This safety-net fires SAFETY_NET_MIC_REENABLE_MS after the LAST onBotStoppedSpeaking so the
   * mic can never get stuck off if the server signal is dropped (e.g. a WS glitch). It is:
   *   - cancelled by `ui.listening` arriving (normal path)
   *   - rearmed (not just cancelled) by each new onBotStoppedSpeaking so the window always
   *     measures from the end of the LAST utterance of the turn, not the first
   *   - cancelled by onBotStartedSpeaking (a new bot turn started before the net fired)
   *   - cancelled on disconnect (botMuted reset in onDisconnected)
   * SAFETY_NET_MIC_REENABLE_MS: 9s — generous enough that a slow tool call + results TTS
   * never hits it during a normal turn, but tight enough that a stuck mic is always freed.
   */
  const SAFETY_NET_MIC_REENABLE_MS = 9_000;
  const botMuteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * R25 — card-scan surface gating state.
   *
   * `scanSurfaceActiveRef`: whether a card-scan surface (source sheet / camera modal / crop-confirm)
   *   is currently on screen. Dedups `setScanSurfaceActive` so the `card-modal` signal fires EXACTLY
   *   once on the true open→close edge (a nested surface transition — sheet → camera — must not
   *   double-signal or bounce the mic).
   * `micBeforeScanRef`: the visitor's mic-enabled intent captured at scan-open, restored on
   *   scan-close so a scan never clobbers a manual mute/pause. `null` when no scan is open.
   *
   * The gate uses the ESTABLISHED mute path (`client.enableMic` + `setMic`), NOT raw track toggles —
   * the R21 mic-track-churn lesson (`voice-drop-mic-track-churn`): releasing/re-acquiring the mic
   * track churns the media stream; muting the existing track does not.
   */
  const scanSurfaceActiveRef = useRef(false);
  const micBeforeScanRef = useRef<boolean | null>(null);

  /**
   * R18 Wave 1.6: warm-up handshake.
   * `warmSessionKeyRef` holds the uuid minted in startWarming() and shared with the subsequent
   * connect() call so warmup + WS target the same Durable Object → same Container.
   * `warmupAbortRef` holds an AbortController that cancels an in-flight warm-up poll when
   * the user clicks "Start over" (or the component unmounts).
   */
  const warmSessionKeyRef = useRef<string | null>(null);
  const warmupAbortRef = useRef<AbortController | null>(null);

  /**
   * R18 Wave 1.7-C2: auto-reconnect state.
   *
   * `autoReconnectRetryCountRef`: number of automatic reconnect attempts made in the current
   *   drop recovery run. Reset to 0 on every clean new session (connect() entry). The counter
   *   lives outside startWarming() so onDisconnected can read it without stale closure issues.
   *
   * `autoReconnectInProgressRef`: true while an automatic reconnect attempt is running. Guards
   *   the onDisconnected path — when we tear down the previous client to retry, onDisconnected
   *   fires again; without this guard the hook would double-enter the retry loop.
   *
   * `connectTimeoutRef`: handle for the CONNECT_TIMEOUT_MS timer set just before
   *   client.connect() is awaited. Cleared on success; fires on timeout → abort + retry.
   */
  const autoReconnectRetryCountRef = useRef(0);
  const autoReconnectInProgressRef = useRef(false);
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Ref to the latest `connect` function. Allows `onDisconnected` (defined inside `connect` as a
   * PipecatClient callback) to call back into `connect` for retry without the closure capturing a
   * stale version and without creating a circular dep in the useCallback dependency array.
   * Populated by a useEffect immediately after connect() is defined below.
   */
  const connectRef = useRef<((key?: string, isAutoReconnect?: boolean) => Promise<void>) | null>(
    null
  );

  /**
   * RC-1 (r18-reconnect-audiodebug): ref to the latest `startWarming` function.
   * The auto-reconnect retry path (inside `connect`'s callbacks/catch-block) calls
   * `startWarmingRef.current?.(retryUuid)` instead of `connectRef.current?.(retryUuid, true)`,
   * ensuring every reconnect goes through the warm-up handshake before opening the WS.
   * Same pattern as `connectRef` — avoids stale closure + circular dep.
   * Populated by a useEffect after startWarming() is defined.
   */
  const startWarmingRef = useRef<((existingKey?: string) => Promise<void>) | null>(null);

  const setStatus = useDemoStore((s) => s.setStatus);
  const setCards = useDemoStore((s) => s.setCards);
  const upsertLine = useDemoStore((s) => s.upsertLine);
  const setMic = useDemoStore((s) => s.setMic);
  const setPaused = useDemoStore((s) => s.setPaused);
  const setLanguage = useDemoStore((s) => s.setLanguage);
  const setConversationId = useDemoStore((s) => s.setConversationId);
  const setLevels = useDemoStore((s) => s.setLevels);
  const setGovernanceEvent = useDemoStore((s) => s.setGovernanceEvent);
  const finalizePartialLines = useDemoStore((s) => s.finalizePartialLines);

  /**
   * R18 client-audio fix — ref-based audio levels (no per-frame store writes).
   *
   * useAudioLevels owns: (1) the shared AudioLevelRef written at the audio-callback rate;
   * (2) a low-frequency (~15fps) setInterval that mirrors user/bot into the Zustand store
   * so any non-orb consumer still sees reasonably fresh values; (3) the two drop-in callbacks
   * (onLocalAudioLevel / onRemoteAudioLevel) for the PipecatClient callbacks below.
   *
   * The VoiceOrb reads levelRef directly inside its own rAF loop — no React re-render per
   * frame. The per-frame Zustand store write (and resulting re-render cascade that starved
   * the audio worklet) is eliminated.
   */
  const {
    levelRef,
    onLocalAudioLevel: _onLocalAudioLevel,
    onRemoteAudioLevel: _onRemoteAudioLevel,
  } = useAudioLevels(setLevels);
  const setSecondsLeft = useDemoStore((s) => s.setSecondsLeft);
  const reset = useDemoStore((s) => s.reset);
  const setSessionRecording = useDemoStore((s) => s.setSessionRecording);
  const applySelectionAck = useDemoStore((s) => s.applySelectionAck);
  const setCloseEmailAck = useDemoStore((s) => s.setCloseEmailAck);
  // R10 — showcase synergy: server-driven scene / contact-field echo / deixis highlight.
  const applyServerSceneStore = useDemoStore((s) => s.applyServerScene);
  // R15 T1 — on connect, leave the S0 attract resting scene for the S1 welcome contact card.
  const connectSceneStore = useDemoStore((s) => s.connectScene);
  const applyContactFieldEcho = useDemoStore((s) => s.applyContactFieldEcho);
  const setHighlightSkus = useDemoStore((s) => s.setHighlightSkus);
  // R20 (U1) — card-affordance pulse: `ui.pulse_card` (§23) draws the eye to the Card control.
  const pulseCard = useDemoStore((s) => s.pulseCard);
  // R22 (K2) — generalized control pulse: `ui.pulse_control {control}` (§29) draws the eye to any
  // pulseable control (the Type control today, for the out-of-flow email/phone capture channel).
  const pulseControl = useDemoStore((s) => s.pulseControl);
  // R12 — category grid (F2) + welcome-captured auto-advance (F4).
  // R17 Q2 — receiveCategories buffers the grid so a payload-before-scene race can't lose it.
  const receiveCategories = useDemoStore((s) => s.receiveCategories);
  const setWelcomeCaptured = useDemoStore((s) => s.setWelcomeCaptured);
  // R13 — S9 end scene: style-snapshot payload for the terminal reveal (U1).
  const setEndScenePayload = useDemoStore((s) => s.setEndScenePayload);
  // R13 — S6 order-review: server-provided per-item qty defaults for voice orders (U4).
  const setOrderReviewQty = useDemoStore((s) => s.setOrderReviewQty);
  // R18 X1 — server-resolved per-item name/image for the S6/S7 order lines (contract §14 W3).
  const mergeOrderReviewItems = useDemoStore((s) => s.mergeOrderReviewItems);
  // R14 — B2 voice-commit parity + B3 spec-sheet send state (incl. no_order).
  const setOrderCommitted = useDemoStore((s) => s.setOrderCommitted);
  const setSpecSheetSendState = useDemoStore((s) => s.setSpecSheetSendState);
  // R11 — synergy Round B: knowledge-gap "noted" card + S8 floor-handoff card (additive).
  const setGapNote = useDemoStore((s) => s.setGapNote);
  const setHandoff = useDemoStore((s) => s.setHandoff);

  /**
   * Map a ui.governance server message to a StoredGovernanceEvent in the store.
   * governance-contract.md: kind = still_there | time_warning | quality_guard | auto_hangup
   */
  const _handleGovernanceMessage = useCallback(
    (kind: string, reason: string, meta?: Record<string, unknown>): void => {
      if (kind === 'still_there') {
        setGovernanceEvent({ type: 'still-there' });
      } else if (kind === 'time_warning') {
        const remaining = typeof meta?.remaining_sec === 'number' ? meta.remaining_sec : 90;
        setSecondsLeft(remaining);
        setGovernanceEvent({
          type: 'time-warning',
          secondsLeft: remaining,
          canExtend: false,
        });
      } else if (kind === 'quality_guard') {
        if (reason === 'suggest_ptt') {
          setGovernanceEvent({
            type: 'quality-guard',
            suggestion: 'push-to-talk',
          });
        } else if (reason === 'suggest_type') {
          setGovernanceEvent({ type: 'quality-guard', suggestion: 'type' });
        }
        // low_confidence: first ambient frame — silent; do nothing (governor escalates if it persists).
        // off_topic and abuse: Gigi speaks the redirect — no overlay needed.
      } else if (kind === 'auto_hangup') {
        // Session is over — clear governance state and disconnect the voice session.
        setSecondsLeft(null);
        void disconnect();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setGovernanceEvent, setSecondsLeft]
    // `disconnect` intentionally omitted: it is stable (depends only on `reset`) and including it
    // would create a circular dep chain (connect → _handleGovernanceMessage → disconnect → reset).
    // The ref-based clientRef.current access inside disconnect is always up-to-date at call time.
  );

  const disconnect = useCallback(async () => {
    // R18 Wave 1.6: cancel any in-flight warm-up poll before disconnecting.
    if (warmupAbortRef.current) {
      warmupAbortRef.current.abort();
      warmupAbortRef.current = null;
    }
    warmSessionKeyRef.current = null;

    // R18 Wave 1.7-C2: stop any in-flight connect timeout and reset auto-reconnect state.
    if (connectTimeoutRef.current !== null) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
    autoReconnectRetryCountRef.current = 0;
    autoReconnectInProgressRef.current = false;

    const client = clientRef.current;
    // R16 V3 — best-effort style-feedback auto-submit on wind-down (hang-up / new-conversation /
    // disconnect). If the visitor TOUCHED the end-scene chips but never tapped Confirm, flush the
    // pending {kept, removed, added} diff over the STILL-LIVE client BEFORE it's torn down, so the
    // declared style read still lands (dev-77 M3). Fire-and-forget; skip an empty (no-delta) diff.
    if (client) {
      const pending = useDemoStore.getState().pendingStyleFeedback;
      if (pending && (pending.removed.length > 0 || pending.added.length > 0)) {
        client.sendClientMessage('style-feedback', pending);
      }
    }
    clientRef.current = null;
    // W1-2: stop the keepalive ping before disconnecting.
    if (pingIntervalRef.current !== null) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    // Mark as clean so onDisconnected transitions to 'idle' (not 'dropped').
    cleanDisconnectRef.current = true;
    if (client) await client.disconnect();
    if (sinkRef.current) sinkRef.current.innerHTML = '';
    reset();
    cleanDisconnectRef.current = false;
  }, [reset]);

  /**
   * Open the voice WS (Pipecat/WebSocket). Accepts an optional pre-warmed sessionKey so the
   * warm-up handshake (startWarming) and the WS upgrade share the same uuid → same Container.
   * When called without a key (legacy path / manual reconnect from 'dropped'), mints a fresh uuid.
   *
   * R18 Wave 1.6: the normal entry path is startWarming() → connect(warmSessionKeyRef.current).
   * The "Reconnect" affordance on the dropped panel calls startWarming() again (new uuid + warmup).
   *
   * R18 Wave 1.7-C2: `isAutoReconnect` is true when this call is made by the bounded retry loop
   * (triggered by an unexpected disconnect). When true:
   *   - The SAME uuid is reused (voice-client-contract.md §1 "resume reconnect") so the server
   *     lands the still-warm container. The server (1.7-S1) handles the old-socket close with
   *     code 4001 gracefully — the client MUST NOT treat 4001 on the old socket as an error.
   *   - autoReconnectInProgressRef is set so onDisconnected knows an intermediate disconnect
   *     during this attempt is expected (retry loop teardown, not a new unexpected drop).
   * When false (user-initiated or startWarming-driven): reset retry counter to 0.
   */
  const connect = useCallback(
    async (preWarmedKey?: string, isAutoReconnect = false) => {
      if (clientRef.current) return;
      cleanDisconnectRef.current = false;

      if (!isAutoReconnect) {
        // Fresh session entry — reset the retry counter.
        autoReconnectRetryCountRef.current = 0;
      }

      // If reconnecting from 'dropped'/'reconnecting', also clear residual governance state and
      // finalize any partial transcript lines from the dropped session (RC-2: they will never
      // receive a completion callback from the dead client, so close them as final now so the
      // new session's lines are appended cleanly alongside visible prior-session context).
      const currentStatus = useDemoStore.getState().status;
      if (currentStatus === 'dropped' || currentStatus === 'reconnecting') {
        useDemoStore.getState().clearGovernanceEvent();
        useDemoStore.getState().setSecondsLeft(null);
        finalizePartialLines();
      }
      setStatus('connecting');
      userLine.current = null;
      botLine.current = null;

      // R30 V3 / R31 — seed the model-aware jitter buffer's WARM-UP RAMP at SESSION START, BEFORE the
      // WS opens, so the first TTS worklet (`_start()` mints one per utterance) reads the right ramp
      // (floor→target). The boot model is not known here yet (the server resolves it from the boot
      // language), so we seed the SHIPPED DEFAULT target (turbo, the en/default model — deeper than
      // flash, so a slower model is never under-buffered on turn 1). R31: playback begins at the
      // ~450ms floor and grows toward that target while playing, so the turn-start latency is the
      // floor, not the full target. The server's active-TTS-model emit (onServerMessage) then
      // confirms/corrects the target; an explicit set-language switch re-runs the same warm-up.
      applyPreBufferForModel(undefined);

      // R32 X4 — seed the bot-audio LOUDNESS LIMITER params at SESSION START (same lifecycle as the
      // pre-roll ramp above), so the first TTS worklet's `_start()` builds the limiter (compressor +
      // makeup gain) from them. Publishes `globalThis.__coldpressLimiter.params`; INERT until the
      // transport patch delta is applied (until then setting the globals changes nothing — playback is
      // exactly today's, never louder/distorted). The limiter tames the 0-headroom peaks (R58: +0.1
      // dBTP) so the +6 dB makeup lifts perceived loudness (−24.5 → ≈ −18 LUFS) WITHOUT clipping — the
      // reason it's a limiter, not a raw gain.
      applyLimiter();

      // R18 Wave 1.6: use the pre-warmed uuid if supplied (warmup + WS must share one uuid so
      // both routes hit the same Durable Object → same Container). Fallback to a fresh uuid for
      // any code path that calls connect() directly without warmup (e.g. manual error retry).
      // W1-4: any uuid here keys a fresh DO container per visit (voice-client-contract.md §1).
      // R18 Wave 1.7-C2: on auto-reconnect the SAME uuid is passed in (same container resume).
      const sessionKey = preWarmedKey ?? crypto.randomUUID();

      const client = new PipecatClient({
        transport: new WebSocketTransport(),
        enableMic: true,
        enableCam: false,
        callbacks: {
          onConnected: () => {
            setStatus('connected');
            setMic(client.isMicEnabled);
            // R15 T1 — leave the S0 attract resting scene for the S1 welcome contact card (the first
            // in-call beat). No-op on a reconnect/resume that's already past attract (mid-session).
            connectSceneStore();
            // R18 Wave1.6b HOTFIX: do NOT call sendClientMessage here. `onConnected` fires at the
            // transport's 'connected' state — BEFORE 'ready' (bot-ready). Calling sendClientMessage
            // now throws "Attempt to call sendClientMessage when transport not in ready state" and
            // fails the entire connect → the kiosk shows "Connection dropped". The keepalive ping +
            // card resync are started AFTER `await client.connect()` resolves (the ready state) —
            // see the try-block below. (The 20s ping previously survived only because its first tick
            // landed after ready; the resync fired synchronously and broke connect.)
          },
          onDisconnected: () => {
            clientRef.current = null;
            // W1-2: stop the keepalive ping (belt-and-suspenders — disconnect() above also clears
            // it, but if the transport disconnects unexpectedly without going through disconnect(),
            // we still need to clean up here).
            if (pingIntervalRef.current !== null) {
              clearInterval(pingIntervalRef.current);
              pingIntervalRef.current = null;
            }
            // R18 Wave 1.7-C2: also clear any connect timeout (if onDisconnected fires before
            // connect() resolved — e.g. the WS was refused).
            if (connectTimeoutRef.current !== null) {
              clearTimeout(connectTimeoutRef.current);
              connectTimeoutRef.current = null;
            }
            // R21: reset the bot-muted flag and cancel any pending safety-net timer on
            // disconnect so the next session starts clean.
            botMutedRef.current = false;
            if (botMuteTimerRef.current !== null) {
              clearTimeout(botMuteTimerRef.current);
              botMuteTimerRef.current = null;
            }

            if (cleanDisconnectRef.current) {
              // User-initiated hang-up: full reset → 'idle'. SW update can now apply.
              reset();
            } else if (autoReconnectInProgressRef.current) {
              // R18 Wave 1.7-C2: this onDisconnected fired as part of tearing down a previous
              // client during an auto-reconnect retry. The retry loop manages state; ignore here.
              autoReconnectInProgressRef.current = false;
            } else {
              // Unexpected WS drop (network, Container eviction, CF timeout).
              // R18 Wave 1.7-C2: auto-reconnect if we haven't exhausted retries.
              // Clear live voice state + governance overlay regardless.
              useDemoStore.getState().clearGovernanceEvent();
              useDemoStore.getState().setSecondsLeft(null);
              useDemoStore.getState().setMic(false);

              const retryCount = autoReconnectRetryCountRef.current;
              if (retryCount < AUTO_RECONNECT_MAX_RETRIES) {
                // Auto-reconnect: reuse the SAME session uuid (voice-client-contract.md §1 +
                // §7 "resume reconnect") so the server routes this WS to the still-warm
                // container (1.7-S1 identity guard). The old socket's 4001 close is an expected
                // artefact of the supersede — not an error (do not surface it).
                // RC-1: call startWarmingRef(retryUuid) instead of connectRef(retryUuid, true)
                // so the retry goes through warm-up — prevents cold-container connect-timeouts.
                const retryUuid = warmSessionKeyRef.current ?? undefined;
                autoReconnectRetryCountRef.current = retryCount + 1;
                const backoffMs = 2 ** retryCount * 1_000; // 1s, 2s, 4s

                useDemoStore.getState().setStatus('reconnecting');

                // Delay before retry (backoff), then attempt reconnect via warm-up.
                // autoReconnectInProgressRef is NOT set here — startWarming handles its own
                // abort/cancel logic; the WarmingScreen shows during the warmup poll window.
                setTimeout(() => {
                  // Only proceed if still in the reconnecting state (user may have hit Start over).
                  if (useDemoStore.getState().status !== 'reconnecting') {
                    return;
                  }
                  void startWarmingRef.current?.(retryUuid);
                }, backoffMs);
              } else {
                // All retries exhausted — show the manual dropped panel.
                // Do NOT full-reset to 'idle' (that clears cards/transcript the visitor
                // can still see and misleads the SW update guard).
                useDemoStore.getState().setStatus('dropped');
              }
            }
          },
          onError: (msg) =>
            setStatus('error', typeof msg?.data === 'string' ? msg.data : 'voice error'),
          // Visitor transcript: Deepgram finalises per pause, so one spoken turn arrives as several
          // fragments. Merge them into ONE continuous line (the DB still stores them per-pause); the
          // line is committed when the agent starts replying (onBotStartedSpeaking).
          onUserTranscript: (d) => {
            const txt = d.text.trim();
            if (!txt) return;
            if (!userLine.current) {
              userLine.current = {
                id: `u-${d.timestamp || Date.now()}`,
                finals: '',
              };
            }
            const ul = userLine.current;
            if (d.final) ul.finals = ul.finals ? `${ul.finals} ${txt}` : txt;
            const display = d.final ? ul.finals : ul.finals ? `${ul.finals} ${txt}` : txt;
            upsertLine({
              id: ul.id,
              speaker: 'visitor',
              text: display,
              final: false,
            });
          },
          // Agent transcript: accumulate TTS text between started/stopped speaking.
          onBotStartedSpeaking: () => {
            const ul = userLine.current; // visitor's turn is over — commit their merged line
            if (ul?.finals) {
              upsertLine({
                id: ul.id,
                speaker: 'visitor',
                text: ul.finals,
                final: true,
              });
            }
            userLine.current = null;
            botLine.current = { id: `a-${Date.now()}`, text: '' };
            // R21: cancel any pending safety-net timer from a prior onBotStoppedSpeaking.
            // If a new bot utterance starts while the safety-net is armed (e.g. filler → gap →
            // real TTS arrives; the safety-net should NOT fire mid-turn), cancel it here.
            // The primary re-enable path is ui.listening (server signal); the safety-net is a
            // last-resort fallback and must not interfere mid-turn.
            if (botMuteTimerRef.current !== null) {
              clearTimeout(botMuteTimerRef.current);
              botMuteTimerRef.current = null;
            }
            // W1-1: half-duplex mic mute — close the speaker→mic feedback loop.
            // Only mute if the user has not already manually muted (paused = false, mic is on).
            // If they've already muted, the mic is off; we don't need to mute again and we must
            // not set botMutedRef so we don't accidentally un-mute them after TTS.
            // Barge-in (W2-8) is parked: muting during TTS disables the user talking over Gigi,
            // but that's the intended Wave 1 tradeoff (echo/self-interrupt >> barge-in UX).
            const { paused, micEnabled } = useDemoStore.getState();
            if (!paused && micEnabled) {
              client.enableMic(false);
              botMutedRef.current = true;
            }
          },
          onBotTtsText: (d) => {
            if (!botLine.current) botLine.current = { id: `a-${Date.now()}`, text: '' };
            // Pipecat (ElevenLabs) streams per-WORD TTS frames, frequently with no surrounding space —
            // a raw `+=` produced jammedtogethertext. Insert a space when joining two word characters,
            // but not before closing punctuation or when either side already has whitespace.
            const prev = botLine.current.text;
            const next = d.text ?? '';
            const needsSpace =
              prev.length > 0 &&
              !/\s$/.test(prev) &&
              next.length > 0 &&
              !/^[\s.,!?;:)\]}'"%]/.test(next) &&
              !/[([{#@$]$/.test(prev);
            botLine.current.text = needsSpace ? `${prev} ${next}` : prev + next;
            upsertLine({
              id: botLine.current.id,
              speaker: 'agent',
              text: botLine.current.text,
              final: false,
            });
          },
          onBotStoppedSpeaking: () => {
            const b = botLine.current;
            if (b?.text)
              upsertLine({
                id: b.id,
                speaker: 'agent',
                text: b.text,
                final: true,
              });
            botLine.current = null;
            // R21 SAFETY NET: arm (or rearm) a long safety-net timer so the mic can never
            // get stuck off if the server's ui.listening signal is dropped.
            //
            // The PRIMARY re-enable path is ui.listening in onServerMessage (below).
            // This timer is a fallback: it fires SAFETY_NET_MIC_REENABLE_MS (9s) after the
            // LAST onBotStoppedSpeaking of the turn. We REARM (cancel + restart) on every
            // onBotStoppedSpeaking so the window always measures from the end of the LAST
            // utterance (not the first) — matches the behaviour we want for multi-utterance
            // turns: the net stays pushed out until the final utterance ends.
            //
            // Do NOT clear botMutedRef here — it stays true until the mic is actually
            // re-enabled (either by ui.listening or the safety net). onBotStartedSpeaking
            // reads botMutedRef to avoid double-muting within the same turn.
            if (botMutedRef.current) {
              // Rearm: cancel any existing safety-net timer, then start a fresh one.
              if (botMuteTimerRef.current !== null) {
                clearTimeout(botMuteTimerRef.current);
              }
              botMuteTimerRef.current = setTimeout(() => {
                botMuteTimerRef.current = null;
                botMutedRef.current = false; // clear ONLY when truly unmuting
                // Re-check user's manual mute/pause state at resume time — they may have paused
                // during TTS. Use the current client ref so this is safe after a disconnect.
                const { paused, micEnabled } = useDemoStore.getState();
                if (!paused && micEnabled !== false) {
                  clientRef.current?.enableMic(true);
                }
              }, SAFETY_NET_MIC_REENABLE_MS);
            }
          },
          // Audio amplitude → the voice-synced animation.
          // R18 client-audio fix: write to the AudioLevelRef only (no store write per frame).
          // The VoiceOrb reads levelRef in its own rAF loop; the store is mirrored at ~15fps.
          onLocalAudioLevel: _onLocalAudioLevel,
          onRemoteAudioLevel: _onRemoteAudioLevel,
          // Agent → kiosk app messages over the RTVI channel.
          // ui.governance → maps Butler's governance contract to the store (wired in Round 5)
          onServerMessage: (data) => {
            const m = data as {
              type?: string;
              payload?: ShowProductsMessage;
              conversation_id?: string;
              language?: string;
              // ui.governance fields (session-governance §6d, governance-contract.md)
              kind?: string;
              reason?: string;
              meta?: Record<string, unknown>;
              // P1 ui.selection_ack fields (voice-client-contract.md §4)
              sku?: string;
              name?: string;
              action?: 'add' | 'remove';
              // P6 ui.contact_ack fields (voice-client-contract.md §6)
              persisted?: boolean;
              // R14 B3 ui.contact_ack spec-sheet send state (R31 §12) — parsed tolerantly incl. no_order.
              state?: string;
              // R14 B2 ui.order_committed fields (R31 §12) — voice-confirmed commit ack.
              order_id?: string;
              // user-llm-text: Pipecat server sends the transcribed user turn into LLM context.
              // We consume and discard — UI transcript is driven by onUserTranscript.
              text?: string;
              // R10 ui.scene fields (showcase-conversation-synergy.md §3)
              scene?: string;
              // R10 ui.contact_field fields (§4.2 voice-fill echo)
              field?: string;
              value?: string;
              source?: string;
              status?: string;
              // R10 ui.highlight fields (§6.2 deixis) — skus to pulse as Gigi narrates.
              skus?: string[];
              // R11 ui.gap_noted fields (§6 #6) — topic Gigi escalated to the team.
              topic?: string;
              // R11 ui.handoff fields (§6 #5, S8) — floor-handoff state + assigned staff.
              staffName?: string;
              // R12 ui.show_categories / mode:'categories' render (F2) — parsed tolerantly.
              mode?: string;
              categories?: unknown;
              items?: unknown;
              cards?: unknown;
              // R30 V3 — the active TTS model rides `tts_model` at the TOP LEVEL of `ui.session`
              // (concierge R56 §41, FROZEN; drives the model-aware jitter buffer). `recording` is the
              // boot shape's session-recording flag (consumed by `ui.session_config` elsewhere; carried
              // here for the boot `ui.session`). Read via `parseActiveTtsModel`.
              recording?: boolean;
              tts_model?: string;
            };
            // R12 (F2): a dedicated category-grid message, OR a `mode:'categories'` variant of the
            // render path. Parsed tolerantly (lib/categoryGrid.ts) so the client works whichever
            // shape R29 ships. Checked BEFORE ui.show_products so a category-shaped render doesn't
            // fall into the product-cards branch.
            // R29 frozen shape (voice-capture-contract.md §9 v4.0):
            //   { type:'ui.categories', payload:{ categories:[{slug, name, count}] } }
            // parseCategoryGrid merges the top-level + nested `payload` scopes, so passing the whole
            // message `m` resolves the frozen shape AND the two compat shapes.
            const categoryGrid =
              m?.type === 'ui.categories' ||
              m?.type === 'ui.show_categories' ||
              (m?.type === 'ui.show_products' && m.mode === 'categories')
                ? parseCategoryGrid(m)
                : null;
            if (categoryGrid) {
              // R17 Q2 — buffer + show. If the scene is still on welcome/attract (the post-confirm
              // auto-show_categories race), the buffer survives and the browse scene re-applies it on
              // arrival (ShowcaseScene). Idempotent on re-emit.
              receiveCategories(categoryGrid);
            } else if (m?.type === 'ui.show_products' && Array.isArray(m.payload?.cards)) {
              setCards(m.payload as ShowProductsMessage);
            } else if (m?.type === 'ui.session') {
              // R30 V3 — the active-TTS-model rides `tts_model` at the TOP LEVEL of `ui.session`
              // (concierge R56 §41, FROZEN — captured fixture `tests/fixtures/wire/r56-ui-session-*.json`).
              // TWO shapes: BOOT `{type:'ui.session', conversation_id, recording, tts_model}` fires once
              // at session start; REMAP `{type:'ui.session', tts_model}` fires on a mid-session language
              // switch carrying ONLY tts_model. The gate no longer requires `conversation_id` (the remap
              // shape has none) — it fires on the type; conversation_id is set only when present.
              if (m.conversation_id) {
                setConversationId(m.conversation_id); // lets an uploaded business card attach to this lead
              }
              // Size the model-aware jitter buffer's warm-up ramp (floor→target) from the emitted model.
              // `parseActiveTtsModel` reads top-level `tts_model` (also tolerant of `model`/nested);
              // `applyPreBufferForModel` sets the session ramp AND pushes it to a live worklet port so a
              // mid-session switch re-runs the SAME warm-up now (playback starts at the floor, grows to
              // the model's target). Absent/null `tts_model` → parse returns null → the buffer keeps its
              // default ramp (graceful).
              const sessionModel = parseActiveTtsModel(m);
              if (sessionModel) applyPreBufferForModel(sessionModel);
            } else if (m?.type === 'ui.language' && m.language) {
              const label = labelForCode(m.language);
              if (label) setLanguage(label, false); // auto-detected — won't override a pinned choice
              // R30 V3 — NOTE: an AUTO-detected language drift does NOT change the TTS model
              // server-side (R53 §M1: "we do NOT rebuild TTS on auto-detected drift — too heavy"), so
              // we deliberately DON'T touch the jitter buffer here. The model-aware depth moves ONLY on
              // session start, the explicit set-language switch, and the server's `ui.session` tts_model
              // emit (above) — the FROZEN R56 §41 wire (there is no `ui.tts_model`/`ui.model` type).
            } else if (m?.type === 'ui.governance') {
              // Session governance events from SessionGovernor (conversation-design.md §6d).
              // governance-contract.md: kind = still_there | time_warning | quality_guard | auto_hangup
              _handleGovernanceMessage(m.kind ?? '', m.reason ?? '', m.meta);
            } else if (m?.type === 'ui.session_config') {
              // ⚑ ADAPTER: awaits Butler ui.session_config WS message (docs/voice-capture-contract.md)
              setSessionRecording(
                (m as { data?: { recording?: boolean } }).data?.recording ?? false
              );
            } else if (m?.type === 'ui.selection_ack' && m.sku && m.action) {
              // P1: server acknowledges a tap-to-select / tap-to-deselect (voice-client-contract.md §4).
              // Drive the chip tray from server-confirmed state (ackedSelections), not purely local.
              applySelectionAck(m.sku, m.name ?? m.sku, m.action);
            } else if (m?.type === 'ui.contact_ack') {
              // Server acknowledges a submit-contact message (voice-client-contract.md §6).
              // Drives the S7 close-card sent/queued state (R12 — the standalone contact form was
              // retired; email capture lives on the close card).
              // R14 B3: also drive the richer spec-sheet send state, which carries the new `no_order`
              // state (send attempted with no committed order → "confirm your selection first", never
              // a false "on its way"). Parsed tolerantly (top-level or nested `payload`, incl. a
              // `state:'no_order'` string). Falls back to the legacy `persisted` boolean.
              const sendState = parseSendState(m);
              if (sendState !== undefined) setSpecSheetSendState(sendState);
              // Keep the legacy boolean in sync for any surface still reading it (close card uses
              // both during the R14 transition). no_order is NOT a persisted send → false.
              setCloseEmailAck(sendState === 'no_order' ? false : (m.persisted ?? false));
            } else if (m?.type === 'ui.order_committed') {
              // R14 B2: a VOICE-confirmed order commit (R31 §12) — the visitor said "it's right" and
              // the server committed place_order against the on-screen selection. Advance the S6
              // review scene to the S7 close card exactly as if Confirm had been tapped (voice-or-tap
              // parity). The ShowcaseScene effect consumes + resets this flag. parseOrderCommitted is
              // tolerant; even a bare `{type:'ui.order_committed'}` sets the flag (the commit already
              // happened server-side — the kiosk must advance regardless of item detail).
              parseOrderCommitted(m); // (validated for shape; the flag alone drives the advance)
              // R18 X1 — the committed items carry the server-resolved `{sku, qty, name, image}`
              // (contract §14 W3); fold their names/images into the meta map so the S7 close recap
              // shows the real piece names even if the cards had scrolled off the cache.
              mergeOrderReviewItems(m.payload ?? m);
              setOrderCommitted(true);
            } else if (m?.type === 'ui.listening') {
              // R21 turn-span mute: server signals that Gigi's turn is truly complete and the
              // visitor may now speak (voice-client-contract.md §12). The server emits this once
              // per bot turn, after the final TTS utterance stops, no tool in-flight, no filler
              // pending. It NEVER fires mid-turn between filler and search results.
              //
              // Re-enable the mic only if WE muted it (botMutedRef) and the user has not
              // manually muted/paused. Cancel the safety-net timer — the primary signal arrived.
              if (botMutedRef.current) {
                if (botMuteTimerRef.current !== null) {
                  clearTimeout(botMuteTimerRef.current);
                  botMuteTimerRef.current = null;
                }
                botMutedRef.current = false;
                const { paused, micEnabled } = useDemoStore.getState();
                if (!paused && micEnabled !== false) {
                  clientRef.current?.enableMic(true);
                }
              }
            } else if (m?.type === 'ui.scene') {
              // R10/R12/R13: server drives a showcase scene change (welcome | browse | order_review
              // | close | end). The store honours welcome-dismissed, order-closed AND session-ended
              // stickiness (lib/scene.ts) — a ui.scene{welcome} never re-traps a "just browsing"
              // visitor, a post-commit ui.scene{browse} can't stomp the close card (F3), and once
              // 'end' is reached nothing reverts it (U1).
              //
              // R19 (SEVENTH-SESSION S6 FIX): resolve the scene name TOLERANTLY (top-level `m.scene`
              // OR nested `payload.scene`/`data.scene`) — dev-81 saw the S6 review scene never render
              // across 3 emissions because the branch gated only on the top-level `m.scene`; a scene
              // name nested under `payload` made `isSceneName(m.scene)` false and skipped the whole
              // branch. Same emission-shape ≠ subscription-shape seam bug that hit categories/contact
              // fields. A ui.scene message with NO valid scene anywhere is a no-op (never falls into
              // another branch — the type is already claimed).
              const sceneName = parseSceneName(m);
              if (sceneName) {
                // F4: a server-driven move OFF welcome is the "capture complete" signal — mark the
                // welcome card captured so it shows its confirmed state + auto-advances (never blank).
                if (sceneName !== 'welcome' && useDemoStore.getState().scene === 'welcome') {
                  setWelcomeCaptured(true);
                }
                // R13 U1: the 'end' scene carries the style-snapshot to reveal — parse it tolerantly
                // (top-level or nested payload) BEFORE the scene applies, so the reveal has its data.
                if (sceneName === 'end') {
                  setEndScenePayload(m.payload ?? m);
                }
                // R13 U4: a voice order arrives as scene:'order_review' carrying per-item qty
                // defaults ("one sample each"). Capture them BEFORE the scene applies so the steppers
                // seed from the server's quantities (parsed tolerantly, top-level or nested payload).
                if (sceneName === 'order_review') {
                  setOrderReviewQty(m.payload ?? m);
                  // R18 X1 — the review items carry the server-resolved `{sku, qty, name, image}`
                  // (contract §14 W3). Fold the names/images in BEFORE the scene applies so the S6
                  // rows render the real names on the first frame, not the "Planters piece" fallback.
                  mergeOrderReviewItems(m.payload ?? m);
                }
                // R22 K3 — the R21 (X1) email-capture-over-end fallback is RETIRED (USER-SPECIFIED).
                // A `capture_email → ui.scene:close` that arrives while the terminal `end` scene is on
                // stage is now a `refused-by-design (session-ended)` — the deliberate goodbye never
                // reverts, and an out-of-flow email/phone ask routes to the Type composer instead (the
                // server emits `ui.pulse_control {control:'type'}`, §29 / K2). Root cause the fallback
                // was retired over: the server's `emit_scene` terminal-end guard swallowed the `close`
                // emission BEFORE the wire whenever `end` was current, so this nonce could never be
                // raised anyway (dev-88 turns 47–53, ×3). `applyServerScene` absorbs / refuses normally.
                applyServerSceneStore(sceneName);
              }
            } else if (m?.type === 'ui.contact_field') {
              // R10/R13: field-level voice/card echo for the S1 welcome + S7 close cards (§4.2).
              // Renders live with a draft treatment until status:'final'. Typed finals are never
              // clobbered (precedence in the store reducer / lib/contactFields.ts).
              //
              // R13 U6 (F4 REGRESSION): the fields are parsed TOLERANTLY (parseContactFieldEcho)
              // from BOTH the top-level and a nested `payload` — the welcome card was staying blank
              // because the server nested them under `payload` (the same emission-shape ≠
              // subscription-shape seam bug that hit ui.categories). Fires per-field, so `name`
              // filling before `company` each lands independently.
              const echo = parseContactFieldEcho(m);
              if (echo) {
                applyContactFieldEcho(echo.field, echo.value, echo.source, echo.status);
              }
            } else if (m?.type === 'ui.highlight' && Array.isArray(m.skus)) {
              // R10 deixis (§6.2): pulse the tile(s) Gigi is describing. The showcase clears the
              // pulse after it plays; an empty array clears any active highlight.
              setHighlightSkus(m.skus.filter((s): s is string => typeof s === 'string'));
            } else if (m?.type === 'ui.pulse_card') {
              // R20 (U1) → R23 (C3) — the LEGACY bare card-pulse cue, kept as a TOLERATED ALIAS. It
              // routes through `pulseCard()`, which now bumps the SAME `card` nonce the generalized
              // `ui.pulse_control {control:'card'}` path uses (one code path). The cue is a bare signal
              // — no payload is read; a fresh nonce re-arms the pulse even if a previous one is settling.
              pulseCard();
            } else if (m?.type === 'ui.pulse_control') {
              // R22 (K2) → R23 (C3) — GENERALIZED control pulse (client contract §31, bound to
              // concierge R43 server §31). When Gigi directs the visitor to a control on the bar — an
              // out-of-flow email via `type` ("tap Type above and send me your email"), OR the Card
              // button via `card` (R43 G2 broadened the cue so Gigi HERSELF can point at Card, not only
              // on visitor card-intent) — she emits this cue. The kiosk draws the eye to that ONE
              // control with the persistent brass-ring breathe. `control` is parsed TOLERANTLY
              // (top-level or nested payload/data — the envelope seam) and validated against the closed
              // pulseable set (`type`, `card`); an unknown/missing control is dropped (never pulsed). A
              // fresh nonce re-arms the pulse mid-settle.
              const control = parsePulseControl(m);
              if (control) pulseControl(control);
            } else if (m?.type === 'ui.gap_noted') {
              // R11 #6: Gigi escalated a question she can't answer ("that's one for the team").
              // Show a quiet, service-not-failure "noted — it'll be in your follow-up ✓" overlay
              // chip on the showcase (auto-dismisses; the showcase owns the timeout). Defensive
              // parse — a bare escalation with no topic still surfaces the reassurance.
              const note = parseGapNote(m.topic);
              if (note) setGapNote(note);
            } else if (m?.type === 'ui.handoff') {
              // R11 #5 (S8): the visitor asked to be walked to a piece on the stand. The server-side
              // handoff (handoffs table + /staff view) is already live; this closes the loop on the
              // showcase — 'requested' → "Finding a colleague…"; 'assigned'/'arriving' →
              // "{staffName} is on the way ✓". Drop malformed payloads (unknown status).
              const handoff = parseHandoff(m.status, m.staffName);
              if (handoff) setHandoff(handoff);
            } else if (m?.type === 'user-llm-text') {
              // R18 console-noise fix: the Pipecat server emits `user-llm-text` frames carrying
              // the transcribed user turn as it is fed into the LLM context. The PipecatClient
              // does not recognise this RTVI message type natively and logs a warning:
              //   "[Pipecat Client] Unrecognized message type user-llm-text"
              // We already handle the user transcript display via onUserTranscript (which fires
              // per Deepgram interim/final frame) so this message is redundant for our UI.
              // No-op: consuming it here silences the warning without altering any state.
              // If a future need arises (e.g. rendering the LLM-ready final text separately),
              // wire m.text here; for now we simply acknowledge the message type.
            }
          },
        },
      });
      clientRef.current = client;

      try {
        // R18 Wave 1.7-C2: connect timeout. Race client.connect() against a timer so a hung
        // handshake (bot never reaches 'ready') is always bounded. If the timer fires first,
        // it rejects with a synthetic error that is caught below and fed into the retry path.
        // The timer is cleared immediately on success so it never fires on a working connect.
        const connectPromise = client.connect({ wsUrl: voiceWsUrl(sessionKey) });
        const timeoutPromise = new Promise<never>((_, reject) => {
          connectTimeoutRef.current = setTimeout(() => {
            connectTimeoutRef.current = null;
            reject(new Error('connect-timeout'));
          }, CONNECT_TIMEOUT_MS);
        });

        // W1-4: pass the per-session UUID so the server keys a fresh Durable Object per visit.
        await Promise.race([connectPromise, timeoutPromise]);

        // R18 Wave 1.7-C2: clear the connect timeout — connect resolved successfully (bot-ready).
        if (connectTimeoutRef.current !== null) {
          clearTimeout(connectTimeoutRef.current);
          connectTimeoutRef.current = null;
        }

        // Successful connect — reset auto-reconnect counter (if we got here via auto-reconnect,
        // the recovery succeeded; future drops start fresh).
        autoReconnectRetryCountRef.current = 0;

        // R18 Wave1.6b HOTFIX: connect() resolves on bot-ready → the transport is NOW in the
        // 'ready' state, so sendClientMessage is safe. (Calling these in onConnected fires before
        // ready and throws, failing the connect → "Connection dropped".)
        // W1-2: 20s keepalive ping (voice-client-contract.md §2) — keeps the CF WS proxy alive
        // (idle cap ~100s; 20s gives 4-miss headroom). Server silently ignores inbound `ping`.
        pingIntervalRef.current = setInterval(() => {
          clientRef.current?.sendClientMessage('ping');
        }, 20_000);
        // P3 CARD RESYNC (voice-client-contract.md §5): recover the first-search card drop without
        // the visitor re-prompting. No-op server-side when nothing has been shown yet.
        clientRef.current?.sendClientMessage('request-showcase-resync');
      } catch (e) {
        // R18 Wave 1.7-C2: clear the connect timeout on any error path.
        if (connectTimeoutRef.current !== null) {
          clearTimeout(connectTimeoutRef.current);
          connectTimeoutRef.current = null;
        }
        // Tear down the client (it may be mid-handshake). Set to null first so onDisconnected
        // sees it as gone and doesn't re-enter the reconnect path on the subsequent disconnect().
        const failedClient = clientRef.current;
        clientRef.current = null;
        // W1-2: also clear the ping interval if connect throws (onConnected never fired).
        if (pingIntervalRef.current !== null) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        // Disconnect the failed client (suppress the onDisconnected reconnect path — the error
        // handler below decides what to do).
        autoReconnectInProgressRef.current = true;
        if (failedClient) {
          try {
            await failedClient.disconnect();
          } catch {
            // ignore — we're already in an error path
          }
        }
        autoReconnectInProgressRef.current = false;

        const errMsg = e instanceof Error ? e.message : String(e);
        const retryCount = autoReconnectRetryCountRef.current;

        if (retryCount < AUTO_RECONNECT_MAX_RETRIES) {
          // R18 Wave 1.7-C2 / RC-1: connect timeout or WS error — treat as a failed attempt and retry
          // via the warm-up path (same uuid) so the next attempt lands a ready container.
          autoReconnectRetryCountRef.current = retryCount + 1;
          const backoffMs = 2 ** retryCount * 1_000;
          useDemoStore.getState().setStatus('reconnecting');
          setTimeout(() => {
            if (useDemoStore.getState().status !== 'reconnecting') return;
            // RC-1: route through startWarmingRef (warm-up + connect) with the same uuid,
            // not connectRef directly — prevents cold-container timeouts on the retry.
            void startWarmingRef.current?.(warmSessionKeyRef.current ?? undefined);
          }, backoffMs);
        } else {
          setStatus('error', errMsg);
        }
      }
    },
    [
      _handleGovernanceMessage,
      _onLocalAudioLevel,
      _onRemoteAudioLevel,
      applySelectionAck,
      applyContactFieldEcho,
      applyServerSceneStore,
      connectSceneStore,
      finalizePartialLines,
      reset,
      setCards,
      receiveCategories,
      setCloseEmailAck,
      setConversationId,
      setGapNote,
      setHandoff,
      setHighlightSkus,
      pulseCard,
      pulseControl,
      setWelcomeCaptured,
      setEndScenePayload,
      setOrderReviewQty,
      mergeOrderReviewItems,
      setOrderCommitted,
      setSpecSheetSendState,
      setLanguage,
      setMic,
      setSessionRecording,
      setStatus,
      upsertLine,
    ]
  );

  // R18 Wave 1.7-C2: keep connectRef up-to-date so the onDisconnected retry callback always
  // calls the latest version of connect (avoids stale closure + circular dep in useCallback).
  // No dep array: intentionally run on every render to keep the ref current.
  useEffect(() => {
    connectRef.current = connect;
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // RC-1 (r18-reconnect-audiodebug): keep startWarmingRef up-to-date so the retry callbacks
  // inside connect() (onDisconnected + catch-block) always call the latest startWarming.
  // Same no-dep-array pattern as connectRef above.
  useEffect(() => {
    startWarmingRef.current = startWarming;
  }); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * R18 Wave 1.6 — Entry point for the warm-up → connect flow.
   *
   * Call this when the visitor taps "Start" (replacing the old `connect()` call on the Talk button).
   * The function:
   *   1. Mints ONE uuid (or reuses `existingKey` — see RC-1 below) and stores it in
   *      `warmSessionKeyRef` (shared for warmup + WS).
   *   2. Transitions status to 'warming' (WarmingScreen covers this window).
   *   3. Starts an AbortController; stores it in `warmupAbortRef` so disconnect() can cancel it.
   *   4. Polls /v1/session/warmup?session=<uuid> via api.warmupSession until ready or timeout.
   *   5a. On ready: auto-proceeds to connect(uuid) — the WS uses the SAME uuid as warmup,
   *       guaranteeing it hits the already-warm Container (same DO key → same Container).
   *   5b. On timeout: transitions to 'warmup-timeout' (graceful "Taking longer — Retry" UI).
   *   5c. On abort (user started over): exits silently; disconnect() already reset to 'idle'.
   *
   * uuid sharing guarantee (the crux of Wave 1.6):
   *   Both `warmupSession(uuid)` → GET /v1/session/warmup?session=<uuid>
   *   and `connect(uuid)` → voiceWsUrl(uuid) → wss://…/v1/ws?session=<uuid>
   *   route through `routeToVoiceSession(env, req, uuid)` on the server, which calls
   *   `getContainer(env.VOICE_SESSION, uuid)`. One uuid → one DO → one Container.
   *   Warmup boots the Container; by the time the WS arrives, the port is already answering.
   *
   * RC-1 (r18-reconnect-audiodebug) — reconnect via warm-up:
   *   `existingKey` is provided by the auto-reconnect retry path (both onDisconnected retry and
   *   connect() catch-block retry). When set, the function REUSES that UUID (same container
   *   resume per voice-client-contract.md §1 + §7) instead of minting a fresh one. The warm-up
   *   poll re-confirms the container is alive before opening the WS — preventing the
   *   cold-container connect-timeout loop that previously occurred on first retry after a drop.
   *   Manual "Reconnect" from the dropped panel still calls startWarming() WITHOUT existingKey
   *   (fresh UUID + full warmup) — unchanged behaviour.
   */
  const startWarming = useCallback(
    async (existingKey?: string) => {
      // Cancel any prior in-flight warmup (e.g. user tapped Start, then Start over, then Start again).
      if (warmupAbortRef.current) {
        warmupAbortRef.current.abort();
        warmupAbortRef.current = null;
      }
      warmSessionKeyRef.current = null;

      // RC-1: reuse existingKey for reconnect resume (same container); mint fresh UUID for new sessions.
      // 1. Get ONE uuid for the entire session (shared by warmup poll + WS upgrade).
      const sessionKey = existingKey ?? crypto.randomUUID();
      warmSessionKeyRef.current = sessionKey;

      // 2. Enter 'warming' state — the WarmingScreen is shown from here.
      setStatus('warming');

      // 3. Create an AbortController so disconnect() can cancel the poll if the user starts over.
      const ac = new AbortController();
      warmupAbortRef.current = ac;

      try {
        // 4. Poll warmup endpoint (2s interval, ~55s budget per voice-client-contract.md §7).
        await warmupSession(sessionKey, ac.signal);

        // 5a. Container is warm — clear the abort controller and proceed to open the voice WS.
        // verify warmSessionKeyRef still matches (guard against a concurrent startWarming() call).
        if (warmSessionKeyRef.current !== sessionKey) return;
        warmupAbortRef.current = null;

        // connect() with the SAME uuid so the WS hits the already-warm Container.
        await connect(sessionKey);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // 5c. User aborted (Start over / disconnect called) — exit silently; state already reset.
          return;
        }
        if (err instanceof Error && err.message === 'warmup-timeout') {
          // 5b. Warmup exceeded budget — show graceful retry UI.
          warmSessionKeyRef.current = null;
          warmupAbortRef.current = null;
          setStatus('warmup-timeout');
          return;
        }
        // Unexpected error during warmup poll — treat as a transient error, show retry UI.
        warmSessionKeyRef.current = null;
        warmupAbortRef.current = null;
        setStatus('error', err instanceof Error ? err.message : String(err));
      }
    },
    [connect, setStatus]
  );

  const toggleMic = useCallback(() => {
    const client = clientRef.current;
    if (!client) return;
    const next = !client.isMicEnabled;
    client.enableMic(next);
    setMic(next);
  }, [setMic]);

  // Pause = hold the whole exchange (mic off); resume turns it back on.
  const pauseToggle = useCallback(() => {
    const client = clientRef.current;
    if (!client) return;
    const paused = !useDemoStore.getState().paused;
    client.enableMic(!paused);
    setPaused(paused);
    setMic(!paused);
  }, [setPaused, setMic]);

  // Typed turn (noisy booth / accessibility) — goes to Gigi as a user message via the RTVI channel.
  //
  // R22 addendum — the §29.2 `user-text` client message. EVERY typed submission is sent as
  // `{ type:'user-text', data:{ text } }` over the RTVI client-message channel (not the transport's
  // `sendText`). The SERVER decides armed-capture (an out-of-flow email/phone the Type composer was
  // pulsed for, §29 / K2) vs normal chat — the composer sends the raw typed text verbatim either way,
  // so the armed-capture channel is no longer inert. `sendClientMessage(type, data)` serialises to the
  // exact frozen wire shape (bound in the tests to `r42-user-text-armed-email` / `r42-user-text-chat`).
  // The transcript-side local echo is UNCHANGED — the visitor still sees their typed line immediately.
  const sendUserText = useCallback(
    (text: string) => {
      const client = clientRef.current;
      const t = text.trim();
      if (!client || !t) return;
      client.sendClientMessage(USER_TEXT_MSG_TYPE, buildUserTextData(t));
      upsertLine({
        id: `u-typed-${Date.now()}`,
        speaker: 'visitor',
        text: t,
        final: true,
      });
    },
    [upsertLine]
  );

  /**
   * R30 V1 — the live composer-draft emit (§40.4b client half). While the visitor types in the S7
   * email/phone fields OR the Type composer, this sends the DEBOUNCED `composer-draft` activity
   * signal `{ type:'composer-draft', data:{ field, value } }` (built by the pure `buildComposerDraftData`).
   * The DEBOUNCE lives at the CALL SITE (CloseScene / TranscriptComposer via `createComposerDraftEmitter`)
   * — this wire fn is a thin, always-immediate emit (mirrors the review-mirror pattern: debounce at the
   * caller, immediate on the wire). The signal HOLDS the server's send/wind-down pending timeout so the
   * flow can't "say done" mid-typing, and surfaces the typed-but-uncommitted value so Gigi confirms it
   * without a re-ask. run_llm=false is the SERVER's read of this message type (it's a context note, not a
   * turn) — the client just emits verbatim. No-op when disconnected (nothing to hold server-side yet).
   *
   * ⚠️ UNVERIFIED binding: concierge R56 owns the finalized §40.4b shape + the server consumer; not
   * landed at R30 authoring time — bound to the DOCUMENTED shape (`voice-capture-contract.md`
   * §40.4·b·client-half). Cross-verify the frozen `data` shape + field enum at integration.
   */
  const sendComposerDraft = useCallback((field: ComposerDraftField, value: string) => {
    const client = clientRef.current;
    if (!client) return;
    client.sendClientMessage(COMPOSER_DRAFT_MSG_TYPE, buildComposerDraftData(field, value));
  }, []);

  // Pin Gigi's reply language (dropdown). Auto-detect still updates the chip until one is pinned.
  //
  // R30 V3 / R31 — the set-language switch reconnects the TTS WS with a possibly-DIFFERENT model (R53
  // §M1: en → turbo, other → multilingual v2). The server's active-TTS-model emit (concierge R56) is
  // the authoritative signal for the new target; but the language→model mapping is deterministic and
  // known client-side (the SHARED `ttsModelForName`, the same resolver the agent-worker uses), so we
  // ALSO re-run the warm-up ramp OPTIMISTICALLY on the switch — the buffer re-targets the instant the
  // reconnect starts rather than one server round-trip later, and the NEXT utterance's worklet warms
  // up from the ~450ms floor to the new model's target (R31: a mid-session model change re-runs the
  // same warm-up). The server's active-model emit (onServerMessage below) then CONFIRMS / corrects it.
  // `applyPreBufferForModel` sets the session-start ramp AND pushes it to a live worklet port if one
  // is already up.
  const selectLanguage = useCallback(
    (name: string) => {
      clientRef.current?.sendClientMessage('set-language', { language: name });
      setLanguage(name, true);
      applyPreBufferForModel(ttsModelForName(name));
    },
    [setLanguage]
  );

  // Business-card capture → R2 + attached to this conversation's lead (server sets the key).
  const uploadCard = useCallback(async (file: File) => {
    const conversationId = useDemoStore.getState().conversationId;
    const qs = conversationId ? `?conversation=${encodeURIComponent(conversationId)}` : '';
    const res = await fetch(`${API_BASE}/v1/card${qs}`, {
      method: 'POST',
      headers: { 'content-type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!res.ok) throw new Error(`upload failed (${res.status})`);
  }, []);

  // Business-card OCR prefill → sends extracted lead fields to Gigi via the RTVI channel.
  // ADAPTER: sends lead prefill data to Gigi via the RTVI channel.
  // Butler to handle 'lead-prefill' client message and merge into the active lead record.
  const prefillLead = useCallback(
    (fields: {
      name?: string;
      company?: string;
      email?: string;
      phone?: string;
    }) => {
      clientRef.current?.sendClientMessage('lead-prefill', fields);
    },
    []
  );

  /**
   * Showcase category browse → emit RTVI client message so Gigi can proactively offer
   * products from the tapped category (Bug 2 fix — round-8-voice).
   *
   * Message name: 'browsed-category' (ws-lifecycle-contract.md — Butler R8 authoring in parallel).
   * Payload: `{ category: string }` where category is the CATEGORIES[].id value.
   * Silently no-ops when not connected (visitor browsing before connecting).
   *
   * NOTE: Butler must handle this message in the agent worker to trigger the proactive response.
   * The message name 'browsed-category' is confirmed from the round-8 dispatch brief.
   */
  const sendBrowsedCategory = useCallback((categoryId: string) => {
    const client = clientRef.current;
    if (!client) return; // not connected — tap is still allowed for visual feedback only
    client.sendClientMessage('browsed-category', { category: categoryId });
  }, []);

  /**
   * P1 — Tap to select a product card (voice-client-contract.md §4).
   * Emits `tap-to-select` RTVI client message so the server's handler fires and Gigi knows
   * which SKUs were tapped without the visitor having to name them aloud.
   * The server acks with `ui.selection_ack { action: 'add' }` which drives ackedSelections.
   * Silently no-ops when not connected — local Zustand still updates for visual feedback.
   */
  const sendTapToSelect = useCallback((sku: string, name: string) => {
    const client = clientRef.current;
    if (!client) return;
    client.sendClientMessage('tap-to-select', { sku, name });
  }, []);

  /**
   * P1 — Tap to deselect a product card (voice-client-contract.md §4).
   * Mirror of sendTapToSelect — removes the SKU from the server-side selection set.
   * The server acks with `ui.selection_ack { action: 'remove' }` which updates ackedSelections.
   */
  const sendTapToDeselect = useCallback((sku: string, name: string) => {
    const client = clientRef.current;
    if (!client) return;
    client.sendClientMessage('tap-to-deselect', { sku, name });
  }, []);

  /**
   * P6 — Submit the visitor's contact details via the WS (voice-client-contract.md §6).
   * Sends `submit-contact` with only the fields the visitor typed (no fabrication). R12: this is
   * the S7 close-card email path; the server acks with `ui.contact_ack { persisted }` which drives
   * `closeEmailAck` (the close card's sent/queued state).
   */
  const sendContactForm = useCallback(
    (fields: {
      name?: string;
      email?: string;
      phone?: string;
      company?: string;
      country?: string;
    }) => {
      const client = clientRef.current;
      if (!client) return;
      // R16 V2 — build via the pure helper so PHONE (and every present field) is carried verbatim
      // and empties are dropped (hard rule). Pins the client half of the lost-phone finding.
      client.sendClientMessage('submit-contact', buildContactFormPayload(fields));
    },
    []
  );

  /**
   * R10 — S1 welcome card contact-submitted (voice-capture-contract.md §R26).
   * Emits `contact-submitted {fields, method}` when the visitor confirms the welcome card
   * (name/company). `method` records the dominant fill mode (card | voice | typed) for the
   * lead record. Only non-empty fields are sent (never fabricate). No-op when disconnected —
   * the welcome card still yields locally so a pre-connect visitor is never blocked.
   */
  const sendContactSubmitted = useCallback(
    (fields: { name?: string; company?: string }, method: 'card' | 'voice' | 'typed') => {
      const client = clientRef.current;
      if (!client) return;
      const payload = Object.fromEntries(
        Object.entries(fields).filter(([, v]) => typeof v === 'string' && v.trim() !== '')
      );
      if (Object.keys(payload).length === 0) return;
      client.sendClientMessage('contact-submitted', { fields: payload, method });
    },
    []
  );

  /**
   * R10 — deixis grid layout (voice-capture-contract.md §R26, §6.2).
   * Emitted on EVERY grid render: `grid-layout {items:[{sku, ordinal}]}`. Gives the server the
   * current on-screen positions so "the second one" / "the brass one on the left" resolve to a
   * SKU. No-op when disconnected (nothing on the server to resolve against yet).
   */
  const sendGridLayout = useCallback((items: Array<{ sku: string; ordinal: number }>) => {
    const client = clientRef.current;
    if (!client) return;
    client.sendClientMessage('grid-layout', { items });
  }, []);

  /**
   * R10 — S6 order confirmed (voice-capture-contract.md §R26, §5).
   * Emits `order-confirmed {items:[{sku, qty}]}` with EXACTLY what the order-review scene shows.
   * This is the explicit visual confirmation that ground-truths the order (audit finding #1);
   * the server commits place_order / send_spec_sheet against this payload.
   */
  const sendOrderConfirmed = useCallback((items: Array<{ sku: string; qty: number }>) => {
    const client = clientRef.current;
    if (!client) return;
    client.sendClientMessage('order-confirmed', { items });
  }, []);

  /**
   * R16 V1 — the live review MIRROR (contract §14). Emits `order-review-updated {items:[{sku, qty}]}`
   * with EXACTLY the S6 screen's current lines. Called on S6 seed AND on every stepper change /
   * removal (a lightweight debounce lives at the call site), plus a final synchronous emit right
   * before `order-confirmed`. The server keeps a per-session mirror; BOTH confirm paths (the voice
   * "that's right" and the Confirm tap) commit from it — so a voice-confirm can never diverge from
   * the screen (dev-77 finding M1). No-op when disconnected. `flush` bypasses debounce at the call
   * site; here the wire emit is always immediate. Items arrive already built (buildReviewMirror).
   */
  const sendOrderReviewUpdated = useCallback((items: Array<{ sku: string; qty: number }>) => {
    const client = clientRef.current;
    if (!client) return;
    client.sendClientMessage('order-review-updated', { items });
  }, []);

  /**
   * R13 U1 — the close card's Done tap. Emits `close-done {}` so the server can deliver the closing
   * beat + drive `ui.scene {scene:'end'}` (the S9 style-snapshot reveal). The kiosk ALSO advances to
   * 'end' locally (the container) so the goodbye lands even if the server signal is slow — Done must
   * never revert to a product screen (user #3b). No-op when disconnected (the local advance covers it).
   */
  const sendCloseDone = useCallback(() => {
    const client = clientRef.current;
    if (!client) return;
    client.sendClientMessage('close-done', {});
  }, []);

  /**
   * R14 B1 — the S9 end-scene style-feedback Confirm. Emits `style-feedback {kept, removed, added}`
   * (R31 §12) so the visitor's DECLARED style corrections persist to the lead + preference graph
   * (the strongest signal — curator's lift discipline). The server acknowledges the corrections
   * aloud ("noted — less gold, more hammered brass") and winds the call down. Best-effort,
   * fire-and-forget; no-op when disconnected (the local thank-you beat still shows).
   */
  const sendStyleFeedback = useCallback(
    (feedback: { kept: string[]; removed: string[]; added: string[] }) => {
      const client = clientRef.current;
      if (!client) return;
      client.sendClientMessage('style-feedback', feedback);
    },
    []
  );

  /**
   * R17 Q3 — the S9 end-scene style-chip state (R34 §16). Emits `style-chips-updated {selected:[…]}`
   * (debounced upstream in EndScene) whenever the visitor's chip selection changes AND once for the
   * initial pre-selected set, so Gigi can discuss the CHIPS accurately before the Confirm (dev-79
   * turns 31–38: "confirm my style choices" → Gigi recited the four PRODUCTS instead of the chips).
   * `selected` is the flat list of currently-selected facet-value tokens across all facets. This is
   * a lightweight context signal, NOT the declared write — the `style-feedback` Confirm (above) is
   * what persists. Best-effort, fire-and-forget; no-op when disconnected.
   */
  const sendStyleChipsUpdated = useCallback((selected: string[]) => {
    const client = clientRef.current;
    if (!client) return;
    client.sendClientMessage('style-chips-updated', { selected });
  }, []);

  /**
   * R10 — shortlist sync (Round 5 #3, finally landing). Emits `shortlist-update {skus}` on tray
   * changes so Gigi's context reflects the visitor's current selection ("strong set for a lobby…").
   * Touch is a full peer to voice — the tray can be driven entirely by tap. No-op when disconnected.
   */
  const sendShortlistUpdate = useCallback((skus: string[]) => {
    const client = clientRef.current;
    if (!client) return;
    client.sendClientMessage('shortlist-update', { skus });
  }, []);

  /**
   * R25 — card-scan surface gating (§38 client half; server half = concierge-gg R47 §37, parallel).
   *
   * Called by `CardScanSource` whenever ANY scan surface (the source sheet, the in-app camera modal,
   * or a distinct crop/confirm step) opens (`true`) or the last one closes (`false`, incl. cancel).
   * Two effects, both idempotent on the true open→close edge (`scanSurfaceActiveRef` dedups so a
   * nested sheet→camera transition never double-signals or bounces the mic):
   *
   *  1. SIGNAL — emit `card-modal {open}` so the server can suppress STT / drop turns while the scan
   *     surface occludes the mic path. The frozen §38 shape is built by the pure `buildCardModalData`
   *     (⚑ stand-in until concierge R47 freezes §37 — cross-verify then). No-op wire when disconnected
   *     (a pre-connect visitor can still scan; there's no server session to gate yet), but the local
   *     mic gate still runs so kiosk audio never reaches a not-yet-connected mic either.
   *  2. LOCAL MIC BELT — mute the mic for the duration of the scan via the ESTABLISHED mute path
   *     (`enableMic(false)` + `setMic(false)`), remembering the visitor's prior intent and RESTORING
   *     it on close. Uses the store's `micEnabled` as the source of truth for prior state (survives a
   *     mid-scan pause). Respects R21: no raw track toggles / no enable→disable churn — one mute on
   *     open, one restore on close.
   */
  const setScanSurfaceActive = useCallback(
    (active: boolean) => {
      // Dedup: only act on the true open→close edge. A sheet→camera→crop transition keeps the
      // surface "active" throughout, so the mic mutes once and the signal fires once.
      if (active === scanSurfaceActiveRef.current) return;
      scanSurfaceActiveRef.current = active;

      const client = clientRef.current;

      if (active) {
        // Remember the visitor's current mic intent, then apply the R21-safe open action (pure).
        const wasMicEnabled = useDemoStore.getState().micEnabled;
        micBeforeScanRef.current = wasMicEnabled;
        if (scanMicActionOnOpen(wasMicEnabled).mic === 'mute') {
          client?.enableMic(false);
          setMic(false);
        }
        client?.sendClientMessage(CARD_MODAL_MSG_TYPE, buildCardModalData(true));
      } else {
        // Apply the R21-safe close action (pure): restore only if the scan muted an enabled mic AND
        // no manual pause intervened during the scan (a mid-scan pause must survive close).
        const { paused } = useDemoStore.getState();
        const action = scanMicActionOnClose(micBeforeScanRef.current, paused);
        micBeforeScanRef.current = null;
        if (action.mic === 'unmute') {
          client?.enableMic(true);
          setMic(true);
        }
        client?.sendClientMessage(CARD_MODAL_MSG_TYPE, buildCardModalData(false));
      }
    },
    [setMic]
  );

  // Disconnect on unmount. Also clear the keepalive interval, warmup poll, and connect timeout
  // as a safety net (disconnect() clears them too, but unmount cleanup runs synchronously while
  // disconnect() is async).
  useEffect(() => {
    return () => {
      if (pingIntervalRef.current !== null) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      // R18 Wave 1.6: abort any in-flight warmup poll on unmount.
      if (warmupAbortRef.current) {
        warmupAbortRef.current.abort();
        warmupAbortRef.current = null;
      }
      // R18 Wave 1.7-C2: clear any in-flight connect timeout on unmount.
      if (connectTimeoutRef.current !== null) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      // R21: cancel any pending safety-net mic re-enable timer on unmount.
      if (botMuteTimerRef.current !== null) {
        clearTimeout(botMuteTimerRef.current);
        botMuteTimerRef.current = null;
      }
      void disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    disconnect,
    startWarming,
    toggleMic,
    pauseToggle,
    sendUserText,
    // R30 V1 — the live composer-draft emit (§40.4b client half); debounced at the call site.
    sendComposerDraft,
    selectLanguage,
    uploadCard,
    prefillLead,
    sendBrowsedCategory,
    sendTapToSelect,
    sendTapToDeselect,
    sendContactForm,
    // R10 — showcase synergy dispatch (additive; all no-op when disconnected).
    sendContactSubmitted,
    sendGridLayout,
    sendOrderConfirmed,
    sendOrderReviewUpdated,
    sendCloseDone,
    sendStyleFeedback,
    sendStyleChipsUpdated,
    sendShortlistUpdate,
    // R25 — card-scan surface gating (emit card-modal + local mic belt via the established mute path).
    setScanSurfaceActive,
    sinkRef,
    /**
     * R18 client-audio fix: shared audio-level ref updated at the audio-callback rate.
     * Pass to VoiceOrb so it can animate imperatively (rAF loop) without React re-renders.
     * Avoids the per-frame store write that previously starved the audio worklet.
     */
    levelRef,
  };
}
